/**
 * Anemoia early access (custom).
 *
 * Drives <anemoia-early-access>: steps the product images on click, posts the form
 * to the waitlist Worker (which creates the Omnisend contact and tags it), then
 * sends the visitor to the unlisted product. The redirect only happens after the
 * Worker confirms, so a failed signup can be retried rather than lost.
 */

class AnemoiaEarlyAccess extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-aea-form]');
    this.submitBtn = this.querySelector('[data-aea-submit]');
    this.successEl = this.querySelector('[data-aea-success]');
    this.errorEl = this.querySelector('[data-aea-error]');
    this.linkEl = this.querySelector('[data-aea-link]');
    this.unlockUrl = this.decodeLink(this.dataset.unlock);

    this.setupGallery();
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
  }

  /** The section base64-encodes the URL. Decoded as UTF-8 so a handle like om-pant-ॐ survives. */
  decodeLink(encoded) {
    if (!encoded) return '';
    try {
      const bytes = Uint8Array.from(atob(encoded), (char) => char.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.warn('Anemoia early access: could not read the product link', error);
      return '';
    }
  }

  /** One image per click or tap. Touch has no hover, so a tap also latches the colour on. */
  setupGallery() {
    const figure = this.querySelector('[data-aea-figure]');
    if (!figure) return;
    const layers = Array.from(figure.querySelectorAll('img.aea__img'));
    if (layers.length < 2) return;

    const latchColour = !window.matchMedia('(pointer: fine)').matches;
    let index = 0;
    figure.classList.add('is-steppable');
    figure.addEventListener('click', () => {
      if (latchColour) figure.classList.add('is-touched');
      layers[index].classList.remove('is-shown');
      index = (index + 1) % layers.length;
      layers[index].classList.add('is-shown');
    });
  }

  async onSubmit(event) {
    event.preventDefault();
    this.hideStatus();

    if (!this.form.reportValidity()) return;

    if (!this.unlockUrl) {
      this.showError('This link isn’t ready yet. Please try again later.');
      return;
    }

    // Honeypot filled: skip the signup but still pay out. Browser autofill can fill
    // this field for a real person, and a bot has no use for the redirect.
    if (this.form.company.value.trim() !== '') {
      this.unlock();
      return;
    }

    const workerUrl = this.dataset.workerUrl;
    if (!workerUrl) {
      this.showError('This form isn’t connected yet. Please try again later.');
      console.warn('Anemoia early access: no data-worker-url set on the section.');
      return;
    }

    this.setBusy(true);
    try {
      const response = await fetch(workerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product: this.dataset.product || '',
          name: this.form.elements.name.value.trim(),
          email: this.form.elements.email.value.trim(),
          gotSwag: this.form.elements.gotSwag.value === 'yes',
          source: 'anemoia-early-access',
          pageUrl: window.location.href,
        }),
      });
      if (!response.ok) throw new Error(`Early access request failed (${response.status})`);
      this.unlock();
    } catch (error) {
      console.error('Anemoia early access submit failed', error);
      this.showError('Something went wrong. Please try again in a moment.');
    } finally {
      this.setBusy(false);
    }
  }

  /** The link stays in the success note, for anyone who comes back with the back button. */
  unlock() {
    if (this.linkEl) {
      this.linkEl.href = this.unlockUrl;
      this.linkEl.hidden = false;
    }
    if (this.successEl) this.successEl.hidden = false;
    this.form.classList.add('is-done');
    window.location.assign(this.unlockUrl);
  }

  setBusy(busy) {
    if (this.submitBtn) this.submitBtn.disabled = busy;
  }

  hideStatus() {
    if (this.successEl) this.successEl.hidden = true;
    if (this.errorEl) this.errorEl.hidden = true;
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }
}

if (!customElements.get('anemoia-early-access')) {
  customElements.define('anemoia-early-access', AnemoiaEarlyAccess);
}
